import { AlignJustify, Shield } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { CharteSignatureForm } from "@/components/home/CharteSignatureForm";
import { useState } from "react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Seo } from "@/components/Seo";

const CharteRepublicaine = () => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  return (
    <div className="min-h-screen flex flex-col bg-[#FFFD4C]">
      <Seo title="La Charte des droits et devoirs du citoyen | EDLR" description="Comprendre et signer la Charte des droits et des devoirs du citoyen français : droits, devoirs et document officiel." path="/charte-republicaine" />
      <Header />

      <main className="flex-1 pt-28 pb-16 bg-gradient-to-b from-[#FFFD4C] via-[#FFFD4C] to-[#F5E84A]">
        <div className="container mx-auto px-4 max-w-6xl">
          {/* Title */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-6">
            <AlignJustify className="w-8 h-8 text-[#1E3A8A]" />
            <h1 className="text-2xl sm:text-3xl font-bold text-[#1E3A8A] break-words">
              La Charte des droits et des devoirs du citoyen français
            </h1>
          </div>

          {/* Top section: intro card (left) + Document Officiel + Signez la charte (right) */}
          <section className="grid md:grid-cols-3 gap-6 items-start">
            {/* Left text card */}
            <div className="md:col-span-2 space-y-6">
              {/* Intro card */}
              <div className="bg-gray-200/80 backdrop-blur-sm rounded-2xl p-4 md:p-5 shadow-sm text-justify text-[#1E3A8A] [&_*]:text-[#1E3A8A] leading-snug text-[14px] space-y-2">
                <h2 className="text-base font-semibold">
                  Qu'est-ce que la Charte des droits et des devoirs du citoyen français ?
                </h2>

                <p>
                  La Charte des droits et des devoirs du citoyen français est prévue à l'article 21-24 du Code civil. Elle a été introduite par la loi n°2011-672 du 16 juin 2011 relative à l'immigration, à l'intégration et à la nationalité, dans le but de rappeler aux nouveaux citoyens français les principes fondamentaux de la République et les engagements liés à l'acquisition de la nationalité française.
                </p>
              </div>

              {/* Two columns: droits / devoirs */}
              <div className="bg-gray-200/80 backdrop-blur-sm rounded-2xl p-4 md:p-5 shadow-sm text-[#1E3A8A] [&_*]:text-[#1E3A8A] leading-snug text-[14px]">
                <p className="font-semibold mb-3">
                  La Charte est un document pédagogique et pratique qui se compose de deux parties principales :
                </p>
                <div className="grid gap-5 md:grid-cols-2">
                  <div>
                    <h5 className="font-semibold flex items-center gap-2 mb-2">
                      <Shield className="w-4 h-4" />
                      1. Les droits du citoyen français :
                    </h5>
                    <ul className="list-disc ml-5 space-y-1.5 marker:text-[#1E3A8A]">
                      <li>Libertés fondamentales : liberté d'expression, de culte, de réunion.</li>
                      <li>Égalité devant la loi : indépendamment de l'origine, du genre ou des croyances.</li>
                      <li>Participation à la vie démocratique : droit de vote et éligibilité.</li>
                    </ul>
                  </div>
                  <div>
                    <h5 className="font-semibold flex items-center gap-2 mb-2">
                      <Shield className="w-4 h-4" />
                      2. Les devoirs du citoyen français :
                    </h5>
                    <ul className="list-disc ml-5 space-y-1.5 marker:text-[#1E3A8A]">
                      <li>Respect des lois : y compris la laïcité, principe fondamental de la République.</li>
                      <li>Contribution à la société : paiement des impôts, solidarité et service national universel.</li>
                      <li>Respect des valeurs républicaines : liberté, égalité, fraternité et respect des autres.</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Bottom paragraph card */}
              <div className="bg-gray-200/80 backdrop-blur-sm rounded-2xl p-4 md:p-5 shadow-sm text-justify text-[#1E3A8A] [&_*]:text-[#1E3A8A] leading-snug text-[14px] space-y-2">
                <p>
                  La Charte des droits et des devoirs du citoyen français est peu connue du grand public puisque son impact est limité aux personnes naturalisées ou devenant françaises par déclaration (par mariage, par exemple), qui la signent dans des contextes spécifiques (cérémonies de naturalisation).
                </p>
                <p>
                  Pourtant son contenu universel et pédagogique mériterait une diffusion plus large. Elle doit en effet :
                </p>
                <ul className="list-disc ml-6 space-y-1.5 marker:text-[#1E3A8A]">
                  <li>Rappeler les droits et devoirs de tout citoyen français : elle constitue un texte clair et pédagogique qui pourrait renforcer la compréhension des responsabilités citoyennes.</li>
                  <li>Symboliser l'engagement républicain : elle rappelle les principes fondamentaux qui lient chaque citoyen français à la République.</li>
                </ul>
              </div>
            </div>

            {/* Right column: stacked cards + image */}
            <div className="space-y-6">
              {/* Document Officiel - blue card */}
              <div className="bg-[#1E3A8A] rounded-2xl p-5 shadow-lg text-white">
                <h3 className="text-lg font-semibold mb-2 text-white">Document Officiel</h3>
                <p className="text-sm mb-4 text-white">
                  Consultez la version officielle de la Charte des droits et des devoirs du citoyen français.
                </p>
                <a
                  href="/Charte_des_droits_et_devoirs_du_citoyen_francais-juin-2020.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center w-full bg-[#D9D9D9] text-[#1E3A8A] py-2.5 px-4 rounded-full font-semibold hover:bg-[#C4C4C4] transition-colors"
                >
                  Accéder à la charte 👉
                </a>
              </div>

              {/* Signez la charte - yellow card */}
              <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                <DialogTrigger asChild>
                  <div className="bg-[#FFFD4C] border border-black/20 rounded-2xl p-5 shadow-lg cursor-pointer hover:bg-[#F5E84A] transition-colors">
                    <h3 className="text-lg font-semibold mb-2 text-[#1E3A8A]">Signez la charte</h3>
                    <p className="text-sm text-[#1E3A8A] mb-4">
                      Soutenez l'action des Enfants de la République en signant La Charte. Vous recevrez gratuitement votre charte "Enfants de la République" personnalisée et numérotée !
                    </p>
                    <button className="w-full bg-[#1E3A8A] text-white py-2.5 px-4 rounded-full font-semibold hover:bg-[#1E3A8A]/90 transition-colors">
                      Je signe la charte
                    </button>
                  </div>
                </DialogTrigger>
                <DialogContent className="sm:max-w-[425px]">
                  <DialogHeader>
                    <DialogTitle>Signer la charte républicaine</DialogTitle>
                  </DialogHeader>
                  <CharteSignatureForm onSuccess={() => setIsDialogOpen(false)} />
                </DialogContent>
              </Dialog>

              {/* Image */}
              <img
                src="/lovable-uploads/40222de6-12ea-4357-a942-a7fe55d59af0.png"
                alt="Liberté, égalité, fraternité, laïcité"
                className="rounded-2xl shadow-lg w-full h-auto object-cover border border-black/20"
              />
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default CharteRepublicaine;
